/**
 * Comprehensive Platform-Specific Analytics Plugin
 *
 * This plugin provides functionality for tracking events across multiple platforms:
 * - Google Analytics 4 (GA4) for all platforms
 * - Adjust and Firebase for non-web platforms only
 *
 * It uses an environment variable to determine the platform type and
 * conditionally imports and uses platform-specific SDKs.
 */

import axios from "axios";
import { cookie, hashString, formatMobileNumber } from "../index";
import auth from "../auth";
import {
	registrationCategories,
	loginCategories,
	depositCategories,
	withdrawCategories,
	casinoCategories,
	generalCategories,
	betSlipCategories,
	jackpotsBetCategories,
	shikishaBetCategories,
	virtualsBetCategories,
	sportsbookBetCategories,
	iframeCategories,
	betikaFastaCasinoCategories,
	experimentCategories,
} from "./config";

// Global variables
let LANGUAGE_CODE = window.navigator.language;
let COUNTRY_CODE = process.env.COUNTRY_CODE;
let GEOLOCATION = Intl.DateTimeFormat().resolvedOptions().timeZone;
const CURRENCY = window.CURRENCY;

// Event source constants
const MINIAPP_UTM_SRC = "mpesa-mini-app";
const DEFAULT_SRC = "mobile-web";

// Cached event source - evaluated once per session for performance
let cachedEventSource = null;

window.dataLayer = window.dataLayer || [];

/**
 * this helper function determines the source (src) attribute for analytics events.
 * Returns "mpesa-mini-app" if user came from mini-app, otherwise "mobile-web".
 * Uses caching to avoid repeated cookie reads on every event.
 * @returns {string} - The source identifier
 */
function getEventSource() {
	// Return cached value if already determined
	if (cachedEventSource !== null) {
		return cachedEventSource;
	}

	try {
		const utmSource = cookie.get("utm_source");
		cachedEventSource = (utmSource && utmSource === MINIAPP_UTM_SRC)
			? MINIAPP_UTM_SRC
			: DEFAULT_SRC;
	} catch (e) {
		// Fallback to default if cookie read fails
		console.warn("[ANALYTICS] Error reading utm_source cookie:", e);
		cachedEventSource = DEFAULT_SRC;
	}

	return cachedEventSource;
}

/**
 * Fetches user information from the server and updates global variables.
 * @returns {Promise<void>}
 */
async function getUserInfo() {
	try {
		const userInfo = (await axios.get(window.USERINFO_URL)).data;
		if (userInfo.language) LANGUAGE_CODE = userInfo.language;
		if (userInfo.country) COUNTRY_CODE = userInfo.country;
		if (userInfo.geo) GEOLOCATION = userInfo.geo;
		updateCookies();
	} catch (e) {
		console.warn("[ANALYTICS] Unable to get user info", e);
	}
}

/**
 * Updates cookies with user and session information.
 */
function updateCookies() {
	const token = localStorage.token ? JSON.parse(localStorage.token) : null;
	if (!token) return;

	const cid = cookie.get("_ga").split(".").slice(2).join(".");
	const ga4cid = cookie.get("_ga").split(".").slice(2).join(".");
	const ga4sid = cookie.get(window.COOKIE_ID).split(".")[2];

	const savedCid = localStorage.cid;
	const savedGa4Cid = localStorage.ga4cid;
	const savedSid = localStorage.ga4sid;

	if (savedSid === ga4sid && savedCid === cid && savedGa4Cid === ga4cid) {
		return;
	}

	axios({
		method: "post",
		url: window.USERINFO_URL,
		headers: {
			"x-ga-cid": cid,
			"x-ga4-cid": ga4cid,
			"x-ga4-sid": ga4sid,
			authorization: `bearer ${token}`,
		},
	})
		.then(() => {
			localStorage.cid = cid;
			localStorage.ga4cid = ga4cid;
			localStorage.ga4sid = ga4sid;
		})
		.catch((err) => {
			console.error("[ANALYTICS] [updateCookies] Error updating cookies", err);
		});
}

/**
 * Decorates the event payload with additional user and context information.
 * @param {Object} payload - The original event payload
 * @returns {Promise<Object>} - The decorated payload
 */
async function decorateDatalayerPayload(payload) {
	const user = auth.getUser();
	let decorated = Object.assign({}, payload);

	if (user) {
		Object.assign(decorated, {
			userId: user.id,
			profile_id: user.id,
			mobileNumber: user.mobile,
			mobile: user.mobile,
			userBalance: user.balance,
			bonusBalance: user.bonus,
			pointsBalance: user.points,
			user: {
				mobile: user.mobile,
				profile_id: user.id,
				id: user.id,
				user_id: user.id,
				wallet_balance: user.balance,
				points_balance: user.points,
				bonus_balance: user.bonus,
			},
		});
	}

	const utm_source = sessionStorage.getItem("affiliate") || "n/a";

	Object.assign(decorated, {
		app: "MOBILE_WEB",
		utm_source: utm_source,
		languageCode: LANGUAGE_CODE,
		country: COUNTRY_CODE,
		GeoLocation: GEOLOCATION,
		currency: CURRENCY,
		environment: process.env.NODE_ENV,
	});

	if (decorated.user && decorated.user.mobile) {
		decorated.user.hashed_msisdn = await hashString(
			formatMobileNumber(decorated.user.mobile)
		);
	}

	return decorated;
}

/**
 * Tracks an event across GA4, and conditionally Adjust and Firebase.
 * @param {Object} categoryObject - The category configuration object
 * @param {string} eventKey - The specific event key
 * @param {Object} data - The event data
 */
async function trackEvent(categoryObject, eventKey, data) {
	if (!categoryObject || !categoryObject[eventKey]) {
		console.warn(`[ANALYTICS] Invalid event configuration for: ${eventKey}`);
		return;
	}

	const eventInfo = categoryObject[eventKey];
	const payload = Object.assign({}, data, {
		event: eventInfo.event,
		eventCategory: eventInfo.eventCategory,
		src: getEventSource(),
	});

	try {
		const finalPayload = ["Login", "Registration"].includes(eventInfo.event)
			? await decorateDatalayerPayload(payload)
			: payload;

		// Push to GA4 data layer (for all platforms)
		dataLayer.push(finalPayload);

		console.log(`[ANALYTICS] Event tracked: ${eventInfo.event}`, finalPayload);
	} catch (e) {
		console.warn(`[ANALYTICS] Error tracking event: ${eventInfo.event}`, e);
	}
}

/**
 * Creates analytics methods for a given category.
 * @param {Object} categories - The category configuration object
 * @returns {Object} - An object containing the created methods
 */
function createAnalyticsMethods(categories) {
	const methods = {};
	Object.keys(categories).forEach((eventName) => {
		methods[eventName] = (data) => {
			trackEvent(categories, eventName, data);
		};
	});
	return methods;
}

/**
 * The main analytics plugin object containing all tracking methods and utilities.
 */
const analyticsPlugin = {
	getUserInfo,
	registration: createAnalyticsMethods(registrationCategories),
	login: createAnalyticsMethods(loginCategories),
	deposit: createAnalyticsMethods(depositCategories),
	withdraw: createAnalyticsMethods(withdrawCategories),
	sportsbookBet: createAnalyticsMethods(sportsbookBetCategories),
	virtualsBet: createAnalyticsMethods(virtualsBetCategories),
	shikishaBet: createAnalyticsMethods(shikishaBetCategories),
	jackpotsBet: createAnalyticsMethods(jackpotsBetCategories),
	betikaFastaBet: createAnalyticsMethods(betikaFastaCasinoCategories),
	betSlip: createAnalyticsMethods(betSlipCategories),
	casino: createAnalyticsMethods(casinoCategories),
	iframe: createAnalyticsMethods(iframeCategories),
	general: createAnalyticsMethods(generalCategories),
	experiment: createAnalyticsMethods(experimentCategories),
};

export default analyticsPlugin;
